import React, { lazy, Suspense, useState } from 'react';
import { NavigationShell, NavTabId } from '@study-tracker/ui';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import { FirstRunSetupScreen } from './components/FirstRunSetupScreen';
import { StudentProfile, Subject } from '@study-tracker/domain';

const LEGACY_PROFILE_NAME = 'Murad Muzadik';
const LEGACY_CANDIDATE_ID = 'LK-2026-PHY';

const DashboardScreen = lazy(() => import('./screens/DashboardScreen').then((module) => ({ default: module.DashboardScreen })));
const SyllabusScreen = lazy(() => import('./screens/SyllabusScreen').then((module) => ({ default: module.SyllabusScreen })));
const TimerScreen = lazy(() => import('./screens/TimerScreen').then((module) => ({ default: module.TimerScreen })));
const AnalyticsScreen = lazy(() => import('./screens/AnalyticsScreen').then((module) => ({ default: module.AnalyticsScreen })));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen').then((module) => ({ default: module.SettingsScreen })));
const PlannerScreen = lazy(() => import('./screens/PlannerScreen').then((module) => ({ default: module.PlannerScreen })));

export const AppContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<NavTabId>('dashboard');
  const { db, error, isReady } = useDatabase();
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [setupStatus, setSetupStatus] = useState<'checking' | 'required' | 'complete' | 'error'>('checking');
  const [setupError, setSetupError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!db || !isReady) return;
    let active = true;
    void Promise.all([db.studentProfile.findAll(), db.subjects.findAll()])
      .then(([profiles, availableSubjects]) => {
        if (!active) return;
        const currentProfile = profiles[0];
        if (!currentProfile) throw new Error('The local student profile is missing.');
        setProfile(currentProfile);
        setSubjects(availableSubjects);
        const isUnconfigured = !currentProfile.fullName.trim()
          || (currentProfile.fullName === LEGACY_PROFILE_NAME && currentProfile.candidateId === LEGACY_CANDIDATE_ID);
        setSetupStatus(isUnconfigured ? 'required' : 'complete');
      })
      .catch((loadError: unknown) => {
        if (!active) return;
        console.error('Unable to prepare first-run setup:', loadError);
        setSetupError(loadError instanceof Error ? loadError.message : String(loadError));
        setSetupStatus('error');
      });
    return () => { active = false; };
  }, [db, isReady]);

  const screenMap: Record<NavTabId, React.ReactNode> = {
    dashboard: <DashboardScreen onNavigate={setActiveTab} />,
    syllabus: <SyllabusScreen />,
    planner: <PlannerScreen />,
    timer: <TimerScreen />,
    analytics: <AnalyticsScreen />,
    settings: <SettingsScreen onOpenPlanner={() => setActiveTab('planner')} />,
  };

  if (error) return <div role="alert" className="m-6 rounded-2xl border border-al-error/40 bg-al-error-container/20 p-5 text-al-error">
    <h1 className="font-display font-semibold">Local database unavailable</h1>
    <p className="mt-2 text-sm">The app could not open its on-device data store: {error.message}</p>
    <button type="button" onClick={() => window.location.reload()} className="mt-4 rounded-full bg-al-error px-4 py-2 text-sm text-black">Reload app</button>
  </div>;

  if (!isReady || setupStatus === 'checking') return <p role="status" className="p-6 text-sm text-al-on-surface-variant">Opening your local study data…</p>;

  if (setupStatus === 'error' || !profile || !db) return <div role="alert" className="m-6 rounded-2xl border border-al-error/40 bg-al-error-container/20 p-5 text-al-error">
    <h1 className="font-display font-semibold">Unable to load your profile</h1>
    <p className="mt-2 text-sm">{setupError || 'The local student profile is unavailable.'}</p>
    <button type="button" onClick={() => window.location.reload()} className="mt-4 rounded-full bg-al-error px-4 py-2 text-sm text-black">Reload app</button>
  </div>;

  if (setupStatus === 'required') {
    return <FirstRunSetupScreen
      db={db}
      profile={profile}
      subjects={subjects}
      onComplete={(savedProfile) => {
        setProfile(savedProfile);
        setSetupStatus('complete');
      }}
    />;
  }

  return (
    <NavigationShell
      activeTab={activeTab}
      onTabChange={setActiveTab}
      candidateName={profile.fullName || 'Student'}
      candidateId={profile.candidateId}
      isSyncing={false}
    >
      <Suspense fallback={<p role="status" className="p-6 text-sm text-al-on-surface-variant">Opening this section…</p>}>{screenMap[activeTab]}</Suspense>
    </NavigationShell>
  );
};

export const App: React.FC = () => {
  return (
    <DatabaseProvider>
      <AppContent />
    </DatabaseProvider>
  );
};
